import {
  Megaphone,
  Search,
  Instagram,
  MessageCircle,
  Mail,
  Route,
  Globe,
  Clapperboard,
  Check,
} from "lucide-react";
import { useSEO } from "../components/SEO";
import { PageHero, Reveal, SectionTag } from "../components/Reveal";
import { BookCallButton, BlueprintButton, ArrowLink } from "../components/CTA";
import { CtaBand } from "../components/CtaBand";

const KEYWORDS = [
  "Hajj packages",
  "Hajj packages from India",
  "Umrah packages",
  "Umrah packages from Mumbai",
  "Umrah packages from Surat",
  "Umrah packages from Ahmedabad",
  "Umrah packages from Hyderabad",
  "Hajj travel agency",
  "Umrah travel agency",
  "Hajj packages near me",
];

const EMAIL_FLOW = [
  "New enquiry",
  "Welcome",
  "Package information",
  "Trust-building content",
  "FAQ",
  "Consultation invitation",
  "Reminder",
  "Re-engagement",
];

const SERVICES = [
  {
    id: "meta-ads",
    icon: Megaphone,
    title: "Meta Ads",
    desc: "Generate targeted enquiries through Facebook and Instagram campaigns, built around the audiences, offers and seasons that matter to Indian Hajj & Umrah agencies.",
    includes: ["Audience strategy", "Campaign setup", "Creative direction", "Lead generation", "Retargeting", "Campaign optimization"],
    cta: "Explore Meta Ads",
  },
  {
    id: "google-ads",
    icon: Search,
    title: "Google Ads",
    desc: "Capture high-intent Indian customers actively searching for Hajj and Umrah travel options, with geographic targeting kept flexible around the cities and states you serve.",
    includes: ["Search campaign strategy", "High-intent keyword targeting", "Flexible geographic targeting", "Landing-page recommendations", "Conversion tracking", "Ongoing optimization"],
    cta: "Explore Google Ads",
    extra: "keywords",
  },
  {
    id: "social-media",
    icon: Instagram,
    title: "Social Media Marketing",
    desc: "Build a professional digital presence that increases trust before the first enquiry, because prospective pilgrims and families may investigate your agency long before they make contact.",
    includes: ["Package communication", "Educational content", "Travel information", "FAQs", "Customer testimonials when genuine", "Destination content", "Pilgrimage preparation", "Agency credibility", "Seasonal campaigns"],
    cta: "Explore Social Media",
    note: "Only genuine testimonials and reviews. Never fabricated proof.",
  },
  {
    id: "whatsapp-marketing",
    icon: MessageCircle,
    title: "WhatsApp Marketing",
    desc: "Turn enquiries into conversations. For many Indian customers, WhatsApp is where the real buying discussion happens, so we structure it like part of your sales system, not an afterthought.",
    includes: ["WhatsApp enquiry flows", "Lead follow-up", "Customer segmentation", "Response structure", "Follow-up messaging", "Re-engagement"],
    cta: "Explore WhatsApp Marketing",
    note: "We build the conversation system. We don't promise automatic bookings.",
  },
  {
    id: "email-marketing",
    icon: Mail,
    title: "Email Marketing",
    desc: "Not every enquiry is ready to book today. Email keeps your agency present and useful while families compare, discuss and decide, improving the probability that legitimate enquiries receive timely, relevant communication.",
    includes: ["Lead nurturing", "Package updates", "Educational sequences", "FAQ campaigns", "Reminder campaigns", "Re-engagement", "Seasonal communications"],
    cta: "Explore Email Marketing",
    extra: "emailFlow",
    note: "Legitimate marketing communications should respect applicable consent requirements.",
  },
  {
    id: "website-development",
    icon: Globe,
    title: "Website Development",
    desc: "A professional, mobile-responsive website structured to build trust and turn visitors and ad clicks into enquiries: packages, testimonials, WhatsApp and enquiry forms working together.",
    includes: ["Professional homepage", "Hajj & Umrah pages", "Packages section", "Enquiry forms", "WhatsApp CTA", "Testimonials section", "Mobile responsive design", "SEO foundations", "Analytics setup"],
    cta: "Explore Website Development",
    note: "One-time project pricing: Basic ₹20,000, Professional ₹40,000, Advanced ₹50,000.",
  },
  {
    id: "on-ground-content-production",
    icon: Clapperboard,
    title: "On-Ground Content Production",
    desc: "One professional content production day that gives your agency 10 polished reels ready for Instagram, Facebook and Meta Ads, so your marketing always has real, credible footage.",
    includes: ["10 reels", "Content planning", "Production direction", "Editing & delivery", "Meta Ads-ready formats", "Package promotion content", "Trust-building content"],
    cta: "Explore Content Production",
    note: "₹30,000 one-time. Locations, travel, production requirements and other agreed expenses are separate unless otherwise agreed.",
  },
  {
    id: "campaign-strategy",
    icon: Route,
    title: "Hajj & Umrah Campaign Strategy",
    desc: "An integrated acquisition strategy that connects every moving part, so advertising, landing pages, WhatsApp and follow-up work as one system instead of disconnected efforts.",
    includes: ["Audience", "Offer", "Creative", "Seasonality", "Acquisition channels", "Landing pages", "WhatsApp", "Follow-up", "Retargeting", "Sales process"],
    cta: "Explore Our Strategy",
  },
];

export default function Services() {
  useSEO(
    "Hajj & Umrah Marketing Services in India | Nexus Catalyst",
    "Meta Ads, Google Ads, social media, WhatsApp and email marketing, plus integrated campaign strategy, built specifically for Hajj & Umrah travel agencies in India."
  );

  return (
    <main data-testid="services-page">
      <PageHero
        tag="Services"
        testid="services-hero"
        crumbs={[{ label: "Services", to: "/services" }]}
        lines={[
          "Marketing built around",
          <>the <span className="text-tan">Hajj &amp; Umrah</span></>,
          "customer journey.",
        ]}
        copy="Eight connected services. One specialization. Everything designed to move an Indian prospect from first impression to a qualified conversation with your team."
      >
        <BookCallButton testid="services-hero-book-call" />
        <BlueprintButton testid="services-hero-audit" />
      </PageHero>

      {SERVICES.map((s, i) => (
        <section
          key={s.id}
          id={s.id}
          className={`${i % 2 ? "bg-white" : "bg-cream"} py-20 md:py-28 relative overflow-hidden`}
          data-testid={`service-section-${s.id}`}
        >
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-xs font-semibold tracking-[0.25em] text-gold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="h-px w-10 bg-gold" />
                  <s.icon size={20} strokeWidth={1.5} className="text-tan" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-charcoal leading-[1.05]">
                  {s.title}
                </h2>
                <p className="mt-5 text-base text-charcoal/65 leading-relaxed">
                  {s.desc}
                </p>
                <ArrowLink to={`/services/${s.id === "social-media" ? "social-media-marketing" : s.id}`} testid={`service-detail-cta-${s.id}`} className="mt-7">
                  {s.cta}
                </ArrowLink>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <div className={`${i % 2 ? "bg-cream" : "bg-white"} border border-charcoal/10 p-8`}>
                  <div className="text-xs font-semibold tracking-[0.25em] uppercase text-tan mb-5">
                    What's Included
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    {s.includes.map((inc) => (
                      <div key={inc} className="flex items-center gap-3">
                        <Check size={15} strokeWidth={2} className="text-gold shrink-0" />
                        <span className="text-sm font-medium text-charcoal/75">{inc}</span>
                      </div>
                    ))}
                  </div>

                  {s.extra === "keywords" && (
                    <div className="mt-8 border-t border-charcoal/10 pt-6">
                      <div className="text-xs font-semibold tracking-[0.25em] uppercase text-tan mb-4">
                        Example Search Intent
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {KEYWORDS.map((k) => (
                          <span key={k} className="border border-charcoal/15 bg-cream px-3 py-1.5 text-[11px] font-medium text-charcoal/65">
                            {k}
                          </span>
                        ))}
                      </div>
                      <p className="mt-4 text-xs text-charcoal/50 leading-relaxed">
                        We validate real search demand and competition before
                        recommending budgets. No invented numbers.
                      </p>
                    </div>
                  )}

                  {s.extra === "emailFlow" && (
                    <div className="mt-8 border-t border-charcoal/10 pt-6">
                      <div className="text-xs font-semibold tracking-[0.25em] uppercase text-tan mb-4">
                        A Typical Nurture Sequence
                      </div>
                      <div className="flex flex-wrap items-center gap-y-3">
                        {EMAIL_FLOW.map((step, j) => (
                          <div key={step} className="flex items-center">
                            <span className="border border-gold/40 bg-cream px-3 py-1.5 text-[11px] font-semibold text-charcoal/70">
                              {step}
                            </span>
                            {j < EMAIL_FLOW.length - 1 && (
                              <span className="mx-2 text-gold text-xs">→</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {s.note && (
                    <p className="mt-6 text-xs text-charcoal/50 leading-relaxed border-t border-charcoal/10 pt-5">
                      {s.note}
                    </p>
                  )}
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      ))}

      <CtaBand
        title="Not Sure Which Service Fits Your Agency?"
        copy="Book a strategy call and we'll map your market, offer and season to the right mix, honestly, without overselling."
        testid="services-cta-band"
      />
    </main>
  );
}
